import { createFileRoute } from '@tanstack/react-router';
import { CircleAlert, Phone } from 'lucide-react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@apps/web/components/ui/alert';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Input } from '@apps/web/components/ui/input';
import { getApiClient } from '@apps/web/lib/api_client';
import {
    getApiErrorDetail,
    isTransparentRateLimitError,
} from '@apps/web/lib/known_api_errors';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import { cn } from '@apps/web/lib/utils';

export const Route = createFileRoute('/$organization-id/demo')({
    loader: async ({ params }) => {
        // Fetch organization info using fetch directly since this is a public route
        const apiUrl = getRuntimeConfig().apiUrl;
        const response = await fetch(
            `${apiUrl}/organizations/${params['organization-id']}/public`,
        );

        if (!response.ok) {
            throw new Error('Organization not found');
        }

        const data = await response.json();
        return { organization: data };
    },
    component: DemoPage,
    head: ({ loaderData }) => ({
        meta: [{ title: `${loaderData?.organization?.name} Phoebe Demo` }],
    }),
    errorComponent: () => (
        <div className="bg-background flex min-h-screen items-center justify-center">
            <Card className="w-full max-w-md">
                <CardContent className="pt-6">
                    <Alert variant="destructive">
                        <CircleAlert className="h-4 w-4" />
                        <AlertDescription>
                            Organization not found. Please check the URL and try
                            again.
                        </AlertDescription>
                    </Alert>
                </CardContent>
            </Card>
        </div>
    ),
});

function DemoPage() {
    const { organization } = Route.useLoaderData();
    const { 'organization-id': organizationId } = Route.useParams();
    const [firstName, setFirstName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [result, setResult] = useState<{
        success: boolean;
        message: string;
        remainingCalls?: { ip: number; phone: number };
    } | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!firstName.trim()) {
            setResult({
                success: false,
                message: 'Please enter your first name',
            });
            return;
        }

        if (!phoneNumber.trim()) {
            setResult({
                success: false,
                message: 'Please enter a phone number',
            });
            return;
        }

        setIsSubmitting(true);
        setResult(null);

        try {
            const data = await getApiClient().post({
                url: '/demo/voice-call',
                mode: 'live',
                body: {
                    organization_id: organizationId,
                    phone_number: phoneNumber,
                    first_name: firstName,
                },
            });
            setResult({
                success: true,
                message: data.message,
                remainingCalls: {
                    ip: data.remaining_calls_ip,
                    phone: data.remaining_calls_phone,
                },
            });
            setPhoneNumber('');
            setFirstName('');
        } catch (error) {
            if (isTransparentRateLimitError(error)) {
                return;
            }
            setResult({
                success: false,
                message:
                    getApiErrorDetail(error) ??
                    (error instanceof Error
                        ? error.message
                        : 'Failed to initiate call. Please try again.'),
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-background flex min-h-screen items-center justify-center p-4">
            <Card className="w-full max-w-md">
                <div className="flex justify-center p-4 pb-0">
                    <img
                        src="/phoebe.svg"
                        alt="Phoebe"
                        className="h-16 w-auto"
                    />
                </div>
                <CardHeader className="pt-2 text-center">
                    <CardTitle className="text-2xl font-bold">
                        Meet {organization.name} Voice Assistant
                    </CardTitle>
                    <CardDescription className="mt-2">
                        <span className="mb-3 block">
                            We use Phoebe to support our schedulers. Phoebe is a
                            voice AI that will call you to confirm shifts, offer
                            you new ones, and answer any questions you might
                            have.
                        </span>
                        <span>
                            Type in your name and number below to get a demo
                            call and see how it works
                        </span>
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label
                                htmlFor="firstName"
                                className="text-foreground/80 mb-1 block text-sm font-medium"
                            >
                                Your First Name
                            </label>
                            <Input
                                id="firstName"
                                type="text"
                                placeholder="John"
                                value={firstName}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                ) => setFirstName(e.target.value)}
                                disabled={isSubmitting}
                                className="w-full"
                                required={true}
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="phone"
                                className="text-foreground/80 mb-1 block text-sm font-medium"
                            >
                                Your Phone Number
                            </label>
                            <Input
                                id="phone"
                                type="tel"
                                placeholder="(555) 123-4567"
                                value={phoneNumber}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                ) => setPhoneNumber(e.target.value)}
                                disabled={isSubmitting}
                                className="w-full"
                                required={true}
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full"
                        >
                            <Phone className="mr-2 h-4 w-4" />
                            {isSubmitting
                                ? 'Initiating Call...'
                                : 'Call Me Now'}
                        </Button>
                    </form>
                    {result && (
                        <Alert
                            variant={result.success ? 'default' : 'destructive'}
                            className={cn(
                                'mt-4',
                                result.success
                                    ? 'border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/50'
                                    : '',
                            )}
                        >
                            <AlertDescription
                                className={cn(
                                    'text-center',
                                    result.success
                                        ? 'text-green-800 dark:text-green-300'
                                        : '',
                                )}
                            >
                                {result.message}
                                {!result.success && result.remainingCalls && (
                                    <p className="mt-1 text-sm">
                                        Remaining calls:{' '}
                                        {Math.min(
                                            result.remainingCalls.ip,
                                            result.remainingCalls.phone,
                                        )}{' '}
                                        of 3 in the next 5 minutes
                                    </p>
                                )}
                            </AlertDescription>
                        </Alert>
                    )}
                    <div className="mt-6 border-t pt-6 text-center">
                        <p className="text-foreground text-sm">
                            Powered by{' '}
                            <a
                                href="https://phoebe.work"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-foreground font-medium hover:underline"
                            >
                                Phoebe
                            </a>
                        </p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
